'use client';

import { JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';

import CreditConfirmationModal from '../_components/CreditConfirmationModal';
import GeneratedVoicePlayer from '../_components/GeneratedVoicePlayer';
import TextInput from '../_components/TextInput';
import VoiceList from '../_components/VoiceList';
import { formatDisplayText } from '../_components/utils';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { TAB_TO_EVENT_TYPE, Tabs, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';

const TextToSpeech = (): JSX.Element => {
    const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const { voices, user } = useVoiceoverContext();

    const { updateState, textToSpeech: tabState } = useVoiceoverTabContext();
    const {
        script,
        calculatedCost,
        generatedAudioUrl,
        generatedAudioHasPlayed,
        generatedAudioVoiceId,
        selectedVoice,
        voiceoverParams: { speed },
        aiContentId,
        lastAiContentId,
    } = tabState;
    // tabState.selectedVoice has a default value, so selectedVoice should always be defined
    // voice used to generate the currently-displayed audio
    const generatedAudioVoice = voices.find((v) => v.voice_id === generatedAudioVoiceId)!;

    return (
        <>
            <CreditConfirmationModal
                isOpen={confirmModalOpen}
                setIsOpen={setConfirmModalOpen}
                creditCost={calculatedCost}
                buttonText="Generate Speech"
                generateRequestBody={() =>
                    JSON.stringify({
                        text: script,
                        voiceId: selectedVoice,
                        speed: (speed as number) / 100,
                        userId: user!.id,
                    })
                }
            />
            <div className="text-center mb-5">
                <Text fontSize="base" fontWeight="regular">
                    Add your script, pick your voice, then click the button to generate your voiceover.
                </Text>
            </div>
            <TextInput
                speed={speed as number}
                script={script ?? ''}
                onSubmit={() => setConfirmModalOpen(true)}
                buttonText="Generate Speech"
                voiceId={selectedVoice}
                calculatedCost={calculatedCost}
            />
            {generatedAudioUrl && (
                <GeneratedVoicePlayer
                    script={script}
                    audioUrl={generatedAudioUrl}
                    hasPlayed={generatedAudioHasPlayed}
                    setHasPlayed={() => updateState({ generatedAudioHasPlayed: true })}
                    title={generatedAudioVoice.name}
                    subtitle={formatDisplayText(generatedAudioVoice.descriptive)}
                    aiContentId={aiContentId}
                    voiceId={generatedAudioVoiceId}
                    aiContentVoiceoverType={TAB_TO_EVENT_TYPE[Tabs.TextToSpeech]}
                    lastAiContentId={lastAiContentId}
                />
            )}
            <div className={twMerge(!generatedAudioUrl && 'mt-[148px]')}>
                <VoiceList
                    setSelectedVoice={(selectedVoice) => updateState({ selectedVoice: selectedVoice })}
                    selectedVoice={selectedVoice}
                    playingVoiceId={playingVoiceId}
                    setPlayingVoiceId={setPlayingVoiceId}
                />
            </div>
        </>
    );
};

export default TextToSpeech;
